'use client';

import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ScrollToTop } from '@/components/shared/ScrollToTop';
import { RootLayout } from '@/layouts/RootLayout';
import { AdminLayout } from '@/layouts/AdminLayout';

import HomePage from '@/pages/HomePage';
import AboutUsPage from '@/pages/AboutUsPage';
import ServicesPage from '@/pages/ServicesPage';
import ServiceDetailPage from '@/pages/ServiceDetailPage';
import InsightsPage from '@/pages/InsightsPage';
import InsightDetailPage from '@/pages/InsightDetailPage';
import LifeAtSimisiPage from '@/pages/LifeAtSimisiPage';
import ContactUsPage from '@/pages/ContactUsPage';
import SgdpPage from '@/pages/SgdpPage';
import CareerDetailPage from '@/pages/CareerDetailPage';
import PersonDetailPage from '@/pages/PersonDetailPage';
import NotFoundPage from '@/pages/NotFoundPage';

import AdminDashboardPage from '@/pages/admin/AdminDashboardPage';
import AdminLoginPage from '@/pages/admin/AdminLoginPage';
import AdminGuidePage from '@/pages/admin/AdminGuidePage';
import AdminMessagesPage from '@/pages/admin/AdminMessagesPage';
import AdminSettingsPage from '@/pages/admin/AdminSettingsPage';
import AdminAboutSettingsPage from '@/pages/admin/AdminAboutSettingsPage';
import AdminTestimonialsPage from '@/pages/admin/AdminTestimonialsPage';
import AdminTestimonialNewPage from '@/pages/admin/AdminTestimonialNewPage';
import AdminTestimonialEditPage from '@/pages/admin/AdminTestimonialEditPage';
import AdminPeoplePage from '@/pages/admin/AdminPeoplePage';
import AdminPersonNewPage from '@/pages/admin/AdminPersonNewPage';
import AdminPersonEditPage from '@/pages/admin/AdminPersonEditPage';
import AdminPersonCategoriesPage from '@/pages/admin/AdminPersonCategoriesPage';
import AdminPersonCategoryNewPage from '@/pages/admin/AdminPersonCategoryNewPage';
import AdminPersonCategoryEditPage from '@/pages/admin/AdminPersonCategoryEditPage';
import AdminServicesPage from '@/pages/admin/AdminServicesPage';
import AdminServiceNewPage from '@/pages/admin/AdminServiceNewPage';
import AdminServiceEditPage from '@/pages/admin/AdminServiceEditPage';
import AdminServiceCategoriesPage from '@/pages/admin/AdminServiceCategoriesPage';
import AdminServiceCategoryNewPage from '@/pages/admin/AdminServiceCategoryNewPage';
import AdminServiceCategoryEditPage from '@/pages/admin/AdminServiceCategoryEditPage';
import AdminInsightsPage from '@/pages/admin/AdminInsightsPage';
import AdminInsightNewPage from '@/pages/admin/AdminInsightNewPage';
import AdminInsightEditPage from '@/pages/admin/AdminInsightEditPage';
import AdminCareersPage from '@/pages/admin/AdminCareersPage';
import AdminCareerNewPage from '@/pages/admin/AdminCareerNewPage';
import AdminCareerEditPage from '@/pages/admin/AdminCareerEditPage';
import AdminFootprintsPage from '@/pages/admin/AdminFootprintsPage';
import AdminFootprintNewPage from '@/pages/admin/AdminFootprintNewPage';
import AdminFootprintEditPage from '@/pages/admin/AdminFootprintEditPage';

function LocaleRedirect() {
  const location = useLocation();
  if (location.pathname === '/id' || location.pathname.startsWith('/id/')) {
    const newPath = location.pathname.replace(/^\/id/, '') || '/';
    return <Navigate to={newPath + location.search + location.hash} replace />;
  }
  return null;
}

export default function App() {
  return (
    <>
      <LocaleRedirect />
      <ScrollToTop />
      <Routes>
        <Route element={<RootLayout />}>
          <Route index element={<HomePage />} />
          <Route path="about-us" element={<AboutUsPage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="services/:slug" element={<ServiceDetailPage />} />
          <Route path="insights" element={<InsightsPage />} />
          <Route path="insights/:slug" element={<InsightDetailPage />} />
          <Route path="life-at-simisi" element={<LifeAtSimisiPage />} />
          <Route path="contact-us" element={<ContactUsPage />} />
          <Route path="sgdp" element={<SgdpPage />} />
          <Route path="career" element={<Navigate to="/life-at-simisi" replace />} />
          <Route path="career/:slug" element={<CareerDetailPage />} />
          <Route path="our-people" element={<Navigate to="/about-us#our-team" replace />} />
          <Route path="our-people/:slug" element={<PersonDetailPage />} />
          <Route path="admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="login" element={<AdminLoginPage />} />
            <Route path="guide" element={<AdminGuidePage />} />
            <Route path="messages" element={<AdminMessagesPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="about-settings" element={<AdminAboutSettingsPage />} />
            <Route path="testimonials" element={<AdminTestimonialsPage />} />
            <Route path="testimonials/new" element={<AdminTestimonialNewPage />} />
            <Route path="testimonials/:id" element={<AdminTestimonialEditPage />} />
            <Route path="people" element={<AdminPeoplePage />} />
            <Route path="people/new" element={<AdminPersonNewPage />} />
            <Route path="people/:id" element={<AdminPersonEditPage />} />
            <Route path="people/categories" element={<AdminPersonCategoriesPage />} />
            <Route path="people/categories/new" element={<AdminPersonCategoryNewPage />} />
            <Route path="people/categories/:id" element={<AdminPersonCategoryEditPage />} />
            <Route path="services" element={<AdminServicesPage />} />
            <Route path="services/new" element={<AdminServiceNewPage />} />
            <Route path="services/:id" element={<AdminServiceEditPage />} />
            <Route path="services/categories" element={<AdminServiceCategoriesPage />} />
            <Route path="services/categories/new" element={<AdminServiceCategoryNewPage />} />
            <Route path="services/categories/:id" element={<AdminServiceCategoryEditPage />} />
            <Route path="insights" element={<AdminInsightsPage />} />
            <Route path="insights/new" element={<AdminInsightNewPage />} />
            <Route path="insights/:id" element={<AdminInsightEditPage />} />
            <Route path="careers" element={<AdminCareersPage />} />
            <Route path="careers/new" element={<AdminCareerNewPage />} />
            <Route path="careers/:id" element={<AdminCareerEditPage />} />
            <Route path="footprints" element={<AdminFootprintsPage />} />
            <Route path="footprints/new" element={<AdminFootprintNewPage />} />
            <Route path="footprints/:id" element={<AdminFootprintEditPage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </>
  );
}
